import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { Tabs } from "antd";
import { ArpsDesignerKind } from "constants/forecasts.constants";
import { ICheckedForecast, SyncWells } from "store/features";
import { selectMapState, useMapStore } from "stores/map";
import styled from "styled-components";
import { toExpectedForecastBackendProductName } from "utils/arps/productMapping";

import useArpsUndoManager from "components/arps/hooks/useArpsUndoManager";
import { convertConstantsToDatabaseUnits } from "components/arps/utils/convertConstantsToDatabaseUnits";
import TypeWellInputTable from "components/arps/widget/TypeWellEditor/tabs/TypeWellInputTable";
import { useSelectedWellFromStore } from "components/data-analyzer/hooks";
import { useUserSettingsDefaultsQuery } from "components/user/queries";

import { EntityKind } from "../../models/entityKind";
import { DEFAULT_DECLINE_TYPE_SETTING } from "../user-settings/constants/userSetting.constants";
import { useUserSettings } from "../user/hooks";
import ForecastingEditorChangeRecords from "./ForecastingEditorChangeRecords";
import { ForecastingEditorHeader } from "./ForecastingEditorHeader";
import useUpdateForecastWell from "./hooks/updateForecastWell";
import useUpdateForecastWellOnTime from "./hooks/updateForecastWellOnTime";
import useBackfillForecast from "./hooks/useBackfillForecast";
import useFetchCumulativeData from "./hooks/useFetchCumulativeData";
import useFetchForecastWell from "./hooks/useFetchForecastWell";
import { FetchWellHierarchyQueryKey } from "./hooks/useFetchWellHierarchy";
import { useForecastingStateDeps } from "./hooks/useForecastingStateDeps";
import usePromoteForecast from "./hooks/usePromoteForecast";
import {
  ForecastWellStatus,
  OnTime,
  UpdateForecastWell,
  WellHierarchyCollection
} from "./models/forecastWell";

const ARPS_EDITOR_KEY = "1";
const PUBLISHED_RECORDS_KEY = "2";
const ARCHIVED_RECORDS_KEY = "3";

export interface CumulativeData {
  oil: number;
  gas: number;
  water: number;
  condensate: number;
  lastProductionDate: string;
  primaryProduct: string;
  steam: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ForecastingProps {
  // wellHierarchy: WellHierarchy;
}

const Forecasting: FC<ForecastingProps> = () => {
  const selectedWell = useSelectedWellFromStore();
  const queryClient = useQueryClient();
  const {
    pdenSource,
    syncWells,
    filterHistory,
    shouldResetForecastZoomMode,
    dispatchSetForecastWell,
    dispatchSetSyncWells,
    dispatchResetForecastZoomMode,
    dispatchSetForecastZoomModeActive,
    dispatchSetFilterState,
    dispatchFilterWellsFromList,
    dispatchSetSelectedWellsRedux
  } = useForecastingStateDeps();
  const { data: forecastWell } = useFetchForecastWell(selectedWell?.Uwi || "");
  const { data: cumulativeData } = useFetchCumulativeData({
    uwi: selectedWell?.Uwi || "",
    pdenSource: parseInt(pdenSource)
  });
  const { mutateAsync: updateForecast } = useUpdateForecastWell();
  const { mutateAsync: updateOnTime } = useUpdateForecastWellOnTime();
  const { mutateAsync: promoteForecast } = usePromoteForecast();
  const { mutateAsync: backfillForecast } = useBackfillForecast();

  const setSelectedEntities = useMapStore((s) => s.setSelectedEntities);
  const setFlyToSpecificPoint = useMapStore((s) => s.setFlyToSpecificPoint);
  const fieldLevelUndoManager = useArpsUndoManager();

  const syncWellsRef = useRef<SyncWells>(syncWells);

  const userSettingsDefaults = useUserSettingsDefaultsQuery();
  const hasForecastLeadActions =
    !!userSettingsDefaults?.data?.FeatureFlag?.ForecastLeadActions?.enabled;

  const mapState = useMapStore(selectMapState);
  const mapStateRef = useRef(mapState);

  const [activeTab, setActiveTab] = useState(ARPS_EDITOR_KEY);
  const [isForecastZoomActive, setIsForecastZoomActive] = useState(false);

  const { declineType: declineTypeSetting } = useUserSettings();
  const declineType = declineTypeSetting?.decline ?? DEFAULT_DECLINE_TYPE_SETTING;

  const filterHistoryRef = useRef(null);

  useEffect(() => {
    syncWellsRef.current = syncWells;
  }, [syncWells]);

  useEffect(() => {
    if (!forecastWell || !syncWellsRef.current) {
      return;
    }
    // Don't update if activeWells already contains forecastWell.uwi
    if (syncWellsRef.current.activeWells?.includes(forecastWell.uwi)) {
      // if forecastWell changed without changing selected well, we still need to update the store
      dispatchSetForecastWell(forecastWell);
      return;
    }
    dispatchSetSyncWells({
      ...syncWellsRef.current,
      activeWells: [forecastWell.uwi]
    });
  }, [forecastWell]);

  // this is used to reset the forecast zoom mode when the user
  // switches the dashboard while it is still active
  useEffect(() => {
    if (shouldResetForecastZoomMode) {
      setIsForecastZoomActive(false);
      dispatchResetForecastZoomMode(false);
      dispatchSetForecastZoomModeActive(false);
      const filterBeforeZoom = JSON.parse(filterHistoryRef.current);
      dispatchSetFilterState(filterBeforeZoom);
      setFlyToSpecificPoint({ ...mapStateRef.current });
      dispatchSelectedEntity();
    }
  }, [shouldResetForecastZoomMode]);

  const dispatchSelectedEntity = () => {
    const uwi = syncWellsRef.current.activeWells[0];
    const obj = {};
    obj[uwi] = { Uwi: uwi };
    // slightly delaying this call as the filterWellsFromList method clears the selected wells,
    // and we need the selected well to be set in order for the Forecasting component to mount/fetch
    setTimeout(() => {
      setSelectedEntities(EntityKind.Well, obj);
      dispatchSetSelectedWellsRedux(obj);
    }, 100);
  };

  useHotkeys(["=", "add"], async () => {
    if (!isForecastZoomActive) {
      mapStateRef.current = mapState;
      if (filterHistory.length > 1) {
        // ref for the filter before zooming in so we can reset it when zooming back out
        filterHistoryRef.current = filterHistory[filterHistory.length - 1];
      }

      dispatchFilterWellsFromList({ wellList: syncWells.activeWells });
      dispatchSetForecastZoomModeActive(true);
      dispatchSelectedEntity();
    } else {
      const filterBeforeZoom = JSON.parse(filterHistoryRef.current);
      dispatchSetFilterState(filterBeforeZoom);
      dispatchSetForecastZoomModeActive(false);
      setFlyToSpecificPoint({ ...mapStateRef.current });
      dispatchSelectedEntity();
    }
    setIsForecastZoomActive(!isForecastZoomActive);
  });

  const handleForecastChange = async (
    item: ICheckedForecast,
    hasErrors?: boolean,
    hasWarnings?: boolean
  ) => {
    try {
      await updateForecast({
        uwi: forecastWell.uwi,
        update: {
          entityName: forecastWell.entityName,
          forecast: {
            segments: item.arps.map((seg) => ({
              ...seg,
              product: toExpectedForecastBackendProductName(seg.product)
            })),
            onTimeArray: item.onTimeArray,
            constants: convertConstantsToDatabaseUnits(item.constants).map((fc) => ({
              ...fc,
              product: toExpectedForecastBackendProductName(fc.product)
            }))
          },
          hasWarnings: !!hasWarnings,
          hasErrors: !!hasErrors,
          approved: forecastWell.approved
        } as UpdateForecastWell
      });
    } catch (error) {
      toast.error("Failed to update forecast");
    }
  };

  const handleArchiveForecast = async () => {
    try {
      const response = await promoteForecast({
        uwis: [forecastWell.uwi],
        status: ForecastWellStatus.Archived
      });
      toast.success(response.message);
    } catch (error) {
      toast.error(`Failed to archive forecast: ${error?.response?.data?.message}`);
    }
  };

  const handlePublishForecast = async () => {
    try {
      const response = await promoteForecast({
        uwis: [forecastWell.uwi],
        status: ForecastWellStatus.Published
      });
      toast.success(response.message);
    } catch (error) {
      toast.error(`Failed to publish forecast: ${error?.response?.data?.message}`);
    }
  };

  const handleDownloadPublishedForecast = async () => {
    try {
      await backfillForecast({
        uwis: [forecastWell.uwi],
        status: ForecastWellStatus.Published
      });
      toast.success("Successfully populated forecast from published version");
    } catch (error) {
      toast.error(
        `Failed to populate forecast from published version: ${error?.response?.data?.message}`
      );
    }
  };

  const handleUnarchiveForecast = async () => {
    try {
      await backfillForecast({
        uwis: [forecastWell.uwi],
        status: ForecastWellStatus.Archived
      });
      toast.success("Successfully populated forecast from archived version");
    } catch (error) {
      toast.error(
        `Failed to populate forecast from archived version: ${error?.response?.data?.message}`
      );
    }
  };

  const handleOnTimeChange = async (onTime: OnTime[]) => {
    try {
      await updateOnTime({
        uwi: selectedWell?.Uwi,
        update: {
          forecast: {
            onTimeArray: onTime
          }
        } as UpdateForecastWell
      });
    } catch {
      toast.error("Unable to update on-time");
    }
  };
  // The hierarchy tree and the forecaster header pull entityName from two
  // different endpoints; for USA wells they disagree (well name vs API14).
  // The hierarchy panel lives under a separate provider, but its data is in the
  // shared React Query cache — look up the tree's name there so the two align.
  const hierarchyName = useMemo(() => {
    if (!forecastWell?.uwi) return undefined;
    return queryClient
      .getQueriesData<WellHierarchyCollection>(FetchWellHierarchyQueryKey)
      .flatMap(([, data]) => data?.leafs ?? [])
      .find((leaf) => leaf.uwi === forecastWell.uwi)?.entityName;
  }, [queryClient, forecastWell?.uwi]);

  return (
    <RootContainer data-testid="widget-forecasting">
      <Header>
        <ForecastingEditorHeader
          entityName={hierarchyName ?? forecastWell?.entityName}
          handleArchiveForecast={handleArchiveForecast}
          handlePublishForecast={handlePublishForecast}
          handleDownloadPublishedForecast={handleDownloadPublishedForecast}
          handleUnarchiveForecast={handleUnarchiveForecast}
          isWidget={true}
          saveStatus={true}
          hideButtons={!hasForecastLeadActions}
          fieldLevelUndoManager={fieldLevelUndoManager}
        />
      </Header>
      {forecastWell && (
        <Tabs
          defaultActiveKey={ARPS_EDITOR_KEY}
          activeKey={activeTab}
          onTabClick={setActiveTab}
          items={[
            {
              key: ARPS_EDITOR_KEY,
              label: "Forecast",
              children: (
                <TypeWellInputWrapper>
                  <TypeWellInputTable
                    wellKey={forecastWell?.uwi}
                    declineType={declineType}
                    selectedTypeWellTitle={""}
                    kind={ArpsDesignerKind.Forecasting}
                    arps={
                      (forecastWell?.forecast as unknown as ICheckedForecast) ??
                      ({ title: forecastWell?.entityName } as ICheckedForecast)
                    }
                    selectedTypeWellRescat={"P+PDP"}
                    onOnTimeChange={handleOnTimeChange}
                    onChange={handleForecastChange}
                    cumulativeData={cumulativeData}
                    fieldLevelUndoManager={fieldLevelUndoManager}
                  />
                </TypeWellInputWrapper>
              )
            },
            {
              key: PUBLISHED_RECORDS_KEY,
              label: "Published Records",
              children: (
                <>
                  <ForecastingEditorChangeRecords
                    forecastId={forecastWell?.uwi}
                    status={ForecastWellStatus.Published}
                    declineType={declineType}
                  />
                </>
              )
            },
            {
              key: ARCHIVED_RECORDS_KEY,
              label: "Archived Records",
              children: (
                <>
                  <ForecastingEditorChangeRecords
                    forecastId={forecastWell?.uwi}
                    status={ForecastWellStatus.Archived}
                    declineType={declineType}
                  />
                </>
              )
            }
          ]}
        />
      )}
      {!forecastWell && (
        <div style={{ marginLeft: "10px" }}>
          Select a well from Well Hierarchy to start...
        </div>
      )}
    </RootContainer>
  );
};

const RootContainer = styled.div`
  width: 100%;
  height: 100%;
  grid-template-rows: auto 1fr;
  display: grid;
  overflow-x: auto;

  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom: 1px solid #d9e1e2;
  }

  .ant-tabs-nav,
  .ant-tabs-nav-list {
    max-width: 100% !important;
    overflow-x: hidden !important;
  }

  .ant-tabs-nav-operations {
    flex-shrink: 0 !important;
  }

  .ant-tabs-tab.ant-tabs-tab-active {
    caret-color: transparent;
  }

  .ant-tabs-nav-list .ant-tabs-tab .ant-tabs-tab-btn {
    font-size: 1.4rem;
    color: #a2aaad;
    letter-spacing: 0.5px;
    padding: 0 15px 0;
  }

  .ant-tabs-nav-list .ant-tabs-tab .ant-tabs-tab-btn:hover {
    color: var(--color-text);
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--color-text);
    font-weight: 700;
  }

  .ant-tabs-nav-list .ant-tabs-tab:first-child .ant-tabs-tab-btn {
    padding-left: 10px;
  }

  .ant-tabs-top > .ant-tabs-nav .ant-tabs-ink-bar {
    height: 3px;
    left: ${(props) => (props.activeKey - 1) * 157}px !important;
  }

  .ant-tabs > .ant-tabs-nav,
  .ant-tabs > div > .ant-tabs-na {
    margin: 0;
  }

  .ant-tabs-ink-bar {
    background: var(--color-primary);
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0;
    padding-left: 6px;
  }

  .ant-tabs {
    min-height: 0;
  }

  .ant-tabs .ant-tabs-content {
    min-width: 0;
    min-height: 200px;
    height: 100%;
  }

  .ant-tabs-content {
    display: grid;
    overflow-y: auto;
    padding-bottom: 10px;
  }

  .ant-tree {
    background: transparent;
  }

  .ant-tree-switcher {
    background: transparent;
  }
`;

const Header = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  width: 100%;
  max-width: 100%;
  height: 48px;
  margin: 0;
  padding: 0;
`;

const TypeWellInputWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  display: grid;
  margin-top: 10px;
`;

export default Forecasting;
